import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { ChevronDown } from "lucide-react";
import { Children, forwardRef, isValidElement, type ReactNode } from "react";

import { splitProps } from "./utils";

import { buttonVariants } from "@/components/ui/button";
import {
  Dialog as DLG,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu as DM,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover as PV,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table as TBL,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs as TB,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

/** Radix Themes triggers wrap a single element via asChild. Mirror that. */
function smartTrigger(Primitive: any) {
  return function Trigger({ children, asChild, ...rest }: any) {
    const single = isValidElement(children) && Children.count(children) === 1;
    return (
      <Primitive asChild={asChild ?? single} {...rest}>
        {children}
      </Primitive>
    );
  };
}

/* ─────────────── DropdownMenu ─────────────── */

function DMContent({
  size: _s,
  variant: _v,
  color: _c,
  highContrast: _hc,
  ...rest
}: any) {
  return <DropdownMenuContent {...rest} />;
}
function DMItem({ color: _c, shortcut, children, ...rest }: any) {
  return (
    <DropdownMenuItem {...rest}>
      {children}
      {shortcut ? (
        <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>
      ) : null}
    </DropdownMenuItem>
  );
}
function DMTriggerIcon({ className, ...rest }: any) {
  return (
    <ChevronDown
      className={cn("ml-1 size-4 opacity-70", className)}
      {...rest}
    />
  );
}

export const DropdownMenu = Object.assign(DM, {
  Root: DM,
  Trigger: smartTrigger(DropdownMenuTrigger),
  TriggerIcon: DMTriggerIcon,
  Content: DMContent,
  Item: DMItem,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioItem: DropdownMenuRadioItem,
  RadioGroup: DropdownMenuRadioGroup,
  Label: DropdownMenuLabel,
  Separator: DropdownMenuSeparator,
  Group: DropdownMenuGroup,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
});

/* ─────────────── Popover ─────────────── */

function PVContent({ size: _s, width, maxWidth, style, ...rest }: any) {
  return <PopoverContent style={{ width, maxWidth, ...style }} {...rest} />;
}
export const Popover = Object.assign(PV, {
  Root: PV,
  Trigger: smartTrigger(PopoverTrigger),
  Content: PVContent,
  Anchor: PopoverAnchor,
});

/* ─────────────── Tabs ─────────────── */

export const Tabs = Object.assign(TB, {
  Root: TB,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
});

/* ─────────────── Table ─────────────── */

function TableRoot({
  variant: _v,
  size: _s,
  layout,
  className,
  style,
  children,
  ...rest
}: any) {
  return (
    <TBL
      className={className}
      style={{ tableLayout: layout, ...style }}
      {...rest}
    >
      {children}
    </TBL>
  );
}
function cellWrapper(Comp: any, extra?: Record<string, unknown>) {
  return function Cell(props: any) {
    const { justify, align, className, children } = props;
    const { style, rest } = splitProps(props);
    const ta =
      justify || align
        ? ({ start: "left", center: "center", end: "right" } as any)[
            justify ?? align
          ]
        : undefined;
    return (
      <Comp
        className={className}
        style={{ textAlign: ta, ...style }}
        {...extra}
        {...rest}
      >
        {children}
      </Comp>
    );
  };
}
function rowWrapper(Comp: any) {
  return function Row({
    align,
    justify: _j,
    className,
    style,
    children,
    ...rest
  }: any) {
    const va = align
      ? (
          {
            start: "top",
            center: "middle",
            end: "bottom",
            baseline: "baseline",
          } as any
        )[align]
      : undefined;
    return (
      <Comp
        className={className}
        style={{ verticalAlign: va, ...style }}
        {...rest}
      >
        {children}
      </Comp>
    );
  };
}
export const Table = Object.assign(TableRoot, {
  Root: TableRoot,
  Header: TableHeader,
  Body: TableBody,
  Row: rowWrapper(TableRow),
  Cell: cellWrapper(TableCell),
  ColumnHeaderCell: cellWrapper(TableHead),
  RowHeaderCell: cellWrapper(TableCell, { scope: "row" }),
});

/* ─────────────── Dialog ─────────────── */

function DialogContentCompat({
  size: _s,
  maxWidth,
  width,
  align: _a,
  style,
  ...rest
}: any) {
  return <DialogContent style={{ maxWidth, width, ...style }} {...rest} />;
}
function DialogTitleCompat({
  weight: _w,
  size: _s,
  color: _c,
  trim: _t,
  ...rest
}: any) {
  return <DialogTitle {...rest} />;
}
function DialogDescriptionCompat({ size: _s, color: _c, ...rest }: any) {
  return <DialogDescription {...rest} />;
}
export const Dialog = Object.assign(DLG, {
  Root: DLG,
  Trigger: smartTrigger(DialogTrigger),
  Content: DialogContentCompat,
  Title: DialogTitleCompat,
  Description: DialogDescriptionCompat,
  Close: DialogClose,
  Overlay: DialogOverlay,
  Portal: DialogPortal,
});

/* ─────────────── AlertDialog (on the primitive, for asChild button-wrapping) ─────────────── */

const AlertDialogRoot = AlertDialogPrimitive.Root;

const ADContent = forwardRef<any, any>(function ADContent(
  { maxWidth, size: _s, align: _a, className, style, children, ...rest },
  ref,
) {
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className,
        )}
        style={{ maxWidth, ...style }}
        {...rest}
      >
        {children}
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
});

function ADTitle({
  weight: _w,
  size: _s,
  color: _c,
  trim: _t,
  className,
  children,
  ...rest
}: any) {
  return (
    <AlertDialogPrimitive.Title
      className={cn("text-base font-semibold", className)}
      {...rest}
    >
      {children}
    </AlertDialogPrimitive.Title>
  );
}
function ADDescription({
  size: _s,
  color: _c,
  className,
  children,
  ...rest
}: any) {
  return (
    <AlertDialogPrimitive.Description
      className={cn("text-sm text-muted-foreground", className)}
      {...rest}
    >
      {children}
    </AlertDialogPrimitive.Description>
  );
}

function ADAction({ children, className, ...rest }: any) {
  if (isValidElement(children)) {
    return (
      <AlertDialogPrimitive.Action asChild {...rest}>
        {children as ReactNode}
      </AlertDialogPrimitive.Action>
    );
  }
  return (
    <AlertDialogPrimitive.Action
      className={cn(buttonVariants(), className)}
      {...rest}
    >
      {children}
    </AlertDialogPrimitive.Action>
  );
}
function ADCancel({ children, className, ...rest }: any) {
  if (isValidElement(children)) {
    return (
      <AlertDialogPrimitive.Cancel asChild {...rest}>
        {children as ReactNode}
      </AlertDialogPrimitive.Cancel>
    );
  }
  return (
    <AlertDialogPrimitive.Cancel
      className={cn(buttonVariants({ variant: "outline" }), className)}
      {...rest}
    >
      {children}
    </AlertDialogPrimitive.Cancel>
  );
}

export const AlertDialog = Object.assign(AlertDialogRoot, {
  Root: AlertDialogRoot,
  Trigger: smartTrigger(AlertDialogPrimitive.Trigger),
  Content: ADContent,
  Title: ADTitle,
  Description: ADDescription,
  Action: ADAction,
  Cancel: ADCancel,
});
